/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/esm/objectWithoutProperties";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";

/** @module react/utils/draggable */
import React from 'react';
import resetClassNames from '../css/reset.module.scss';
import { withOnDrag } from '../hocs/with-on-drag.js';
import { mergeClassNames } from '../modules/merge-class-names.js';
import { mergeStyles } from '../modules/merge-styles.js'; // TODO: CREATE withPositionBoundaries HOC (TO PREVENT EXITING PARENT ELEMENT VIA POSITION PROPERTIES).

var Div = withOnDrag('div');

/**
 * @typedef draggablePropsType
 * @property {boolean} [noLimit=false] - Allow element to be dragged outside of parent element.
 * @property {boolean} [isolate=true] - Prevent gestures propagation.
 * @property {*} [children=null] - React prop.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @classdesc A draggable component.
 */
var Draggable = /*#__PURE__*/function (_React$Component) {
  _inherits(Draggable, _React$Component);

  var _super = _createSuper(Draggable);

  /**
   * @description Constructor.
   * @param {draggablePropsType} [props] - Component props.
   */
  function Draggable(props) {
    var _context, _context2;

    var _this;

    _classCallCheck(this, Draggable);

    _this = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this), "elementRef", void 0);

    _defineProperty(_assertThisInitialized(_this), "containerHeight", void 0);

    _defineProperty(_assertThisInitialized(_this), "containerWidth", void 0);

    _this.elementRef = null;
    _this.containerHeight = 0;
    _this.containerWidth = 0;
    _this.state = {
      position: null,
      top: null,
      left: null
    }; // BIND METHODS

    _assertThisInitialized(_this).onDrag = _bindInstanceProperty(_context = _this.onDrag).call(_context, _assertThisInitialized(_this));
    _assertThisInitialized(_this).setElementRef = _bindInstanceProperty(_context2 = _this.setElementRef).call(_context2, _assertThisInitialized(_this));
    return _this;
  }
  /**
   * @instance
   * @description React lifecycle.
   * @returns {void} Void.
   */


  _createClass(Draggable, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.containerHeight = this.elementRef.offsetParent.clientHeight;
      this.containerWidth = this.elementRef.offsetParent.clientWidth;
      this.setState({
        position: 'absolute',
        top: this.props.noLimit || this.elementRef.offsetTop + this.elementRef.offsetHeight <= this.containerHeight && this.elementRef.offsetTop >= 0 ? this.elementRef.offsetTop : 0,
        left: this.props.noLimit || this.elementRef.offsetLeft + this.elementRef.offsetWidth <= this.containerWidth && this.elementRef.offsetLeft >= 0 ? this.elementRef.offsetLeft : 0
      });
    }
    /**
     * @instance
     * @description Callback for drag event.
     * @param {Object<string,*>} e - Triggering event.
     * @returns {void} Void.
     */

  }, {
    key: "onDrag",
    value: function onDrag(e) {
      var _this2 = this,
          _context3;

      this.setState(_bindInstanceProperty(_context3 = function _context3(prevState) {
        _newArrowCheck(this, _this2);

        var limitTop = this.containerHeight - this.elementRef.offsetHeight;
        var limitLeft = this.containerWidth - this.elementRef.offsetWidth;
        var newTop = prevState.top + e.deltaY;
        var newLeft = prevState.left + e.deltaX;

        if (!this.props.noLimit) {
          newTop = newTop > limitTop ? limitTop : newTop;
          newTop = newTop < 0 ? 0 : newTop;
          newLeft = newLeft > limitLeft ? limitLeft : newLeft;
          newLeft = newLeft < 0 ? 0 : newLeft;
        }

        return {
          top: newTop,
          left: newLeft
        };
      }).call(_context3, this));
    }
    /**
     * @instance
     * @description Save component reference.
     * @param {Object<string,*>} ref - Component reference.
     * @returns {void} Void.
     */

  }, {
    key: "setElementRef",
    value: function setElementRef(ref) {
      // SAVE REF
      this.elementRef = ref;
    }
    /**
     * @instance
     * @description React render method.
     * @returns {Object<string,*>} React component.
     */

  }, {
    key: "render",
    value: function render() {
      var _this$props = this.props,
          children = _this$props.children,
          isolate = _this$props.isolate,
          noLimit = _this$props.noLimit,
          style = _this$props.style,
          className = _this$props.className,
          customStyles = _this$props.customStyles,
          customClassNames = _this$props.customClassNames,
          globalTheme = _this$props.globalTheme,
          other = _objectWithoutProperties(_this$props, ["children", "isolate", "noLimit", "style", "className", "customStyles", "customClassNames", "globalTheme"]);

      var _this$state = this.state,
          position = _this$state.position,
          top = _this$state.top,
          left = _this$state.left;
      var globalStyles = globalTheme ? Draggable.globalStyles[globalTheme] || {} : {};
      var globalClassNames = globalTheme ? Draggable.globalClassNames[globalTheme] || {} : {};
      var mergedStyles = {
        root: mergeStyles(globalStyles.root, customStyles.root, style, {
          position: position,
          top: top,
          left: left
        })
      };
      var mergedClassNames = {
        root: mergeClassNames(resetClassNames.root, globalClassNames.root, customClassNames.root, className)
      };
      return /*#__PURE__*/React.createElement(Div, _extends({}, other, {
        isolate: isolate,
        onDrag: this.onDrag,
        ref: this.setElementRef,
        style: mergedStyles.root,
        className: mergedClassNames.root
      }), children);
    }
  }]);

  return Draggable;
}(React.Component);

_defineProperty(Draggable, "globalStyles", {
  default: {
    root: {}
  }
});

_defineProperty(Draggable, "globalClassNames", {
  default: {
    root: ''
  }
});

_defineProperty(Draggable, "defaultProps", {
  noLimit: false,
  isolate: true,
  children: null,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  globalTheme: 'default'
});

export { Draggable };